import { ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';
import {
	Experience,
	EXPERIENCE_ID,
	ExperienceCheckTimeout,
} from '@atlaskit/editor-common/experiences';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import type { SetThinkingStateAction } from '../editor-actions/types';
import type { ThinkingPluginState } from '../pm-plugins/thinking-plugin';

// A fixed reliability deadline: a review that never reaches a terminal status must fail.
export const TOOLBAR_REVIEW_TIMEOUT_MS = 300_000;

export type ToolbarReviewExperience = {
	destroy: () => boolean;
	failure: (reason: string) => boolean;
	onThinkingStatusChange: (state: ThinkingPluginState) => void;
	start: (options: Parameters<SetThinkingStateAction>[1]) => void;
};

/** Tracks toolbar Review attempts through the thinking bar's committed terminal status. */
export function createToolbarReviewExperience(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
): ToolbarReviewExperience {
	let isToolbarReview = false;
	const experience = new Experience(EXPERIENCE_ID.MENU_ACTION, {
		action: 'generate',
		actionSubjectId: ACTION_SUBJECT.AI_SUGGESTIONS,
		// Count every attempt during the initial rollout, including toolbar-review retries.
		sampleRate: 1,
		dispatchAnalyticsEvent: (event) => api?.analytics?.actions.fireAnalyticsEvent(event),
		checks: [new ExperienceCheckTimeout({ durationMs: TOOLBAR_REVIEW_TIMEOUT_MS })],
	});

	return {
		start: (options: Parameters<SetThinkingStateAction>[1]) => {
			if (options?.entryPoint) {
				isToolbarReview = options.entryPoint === 'primaryToolbar';
			} else if (!options?.isRetry) {
				isToolbarReview = false;
			}
			if (!isToolbarReview) {
				experience.abort({ reason: 'reviewReplaced' });
				return;
			}
			experience.start({
				forceRestart: true,
				method: 'primaryToolbar',
				metadata: { isRetry: options?.isRetry ?? false },
			});
		},
		onThinkingStatusChange: ({ status, error }: ThinkingPluginState) => {
			if (status === 'complete') {
				experience.success();
			} else if (status === 'error') {
				experience.failure({ reason: 'generationFailed', metadata: { template: error?.template } });
			} else if (status === 'idle') {
				isToolbarReview = false;
				experience.abort({ reason: 'thinkingStopped' });
			}
		},
		failure: (reason: string) => experience.failure({ reason }),
		destroy: () => experience.abort({ reason: 'editorDestroyed' }),
	};
}
